/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, CircularProgress, Skeleton, Stack } from "@mui/material";
import { useParams } from "react-router-dom";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import { ExpandedWorkOrder, WorkOrder, WorkOrderTask } from "../../utils/Interfaces";
import WorkOrderHeader from "./WorkOrderHeader";
import TechnicianEntryGroup from "./technician-entry/TechnicianEntryGroup";
import { workOrdersStatusTitles } from "../../utils/Constants";
import { useEditWorkOrderState, useWorkOrderDetails } from "./workOrderHooks";
import ConfirmModal from "../../components/confirmation/ConfirmModal";

const OfficeWorkOrder = () => {
    const { workOrderId } = useParams();

    const { data: workOrderDetails, status: workOrderDetailsStatus } = useWorkOrderDetails(parseInt(workOrderId ?? "0"));
    const { mutate: workOrderAction, status: workOrderActionStatus } = useEditWorkOrderState(parseInt(workOrderId ?? "0"));

    const [showConfirmCompleteModal, setShowConfirmCompleteModal] = useState<boolean>(false);

    if (!workOrderDetails || workOrderDetailsStatus !== "success") {
        return (
            <Box>
                <StyledBreadcrumbs
                    paths={[{ text: "Techwise Portal", route: "/" }, { text: "Service", route: "/?section=office" }, null, null]}
                />
                <Stack direction="row" justifyContent="center" sx={{ mt: 20 }}>
                    <CircularProgress size={40} />
                </Stack>
            </Box>
        );
    }

    const workOrder: WorkOrder = workOrderDetails.work_order;
    const tasks: WorkOrderTask[] = workOrderDetails.tasks;

    function handleCompleteWorkOrder() {
        let allTasksComplete: boolean = true;
        tasks.map((task: WorkOrderTask) => {
            if (task.completed_timestamp === null) {
                allTasksComplete = false;
            }
            return null;
        });

        if (!allTasksComplete) {
            setShowConfirmCompleteModal(true);
        } else {
            workOrderAction("complete");
        }
    }

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Service", route: "/?section=office" },
                    {
                        text: `Work Orders - ${workOrdersStatusTitles[workOrder.status as keyof typeof workOrdersStatusTitles]}`,
                        route: `/work-orders?status=${workOrder.status}`,
                    },
                    { text: `#${workOrder.ticket_no}`, route: `/work-orders/${workOrderId}` },
                ]}
            />

            <ConfirmModal
                description="The following work order contains punchlist items that are incomplete, are you sure you want to complete the task?"
                variant="warning"
                onAccept={() => workOrderAction("complete")}
                open={showConfirmCompleteModal}
                handleClose={() => setShowConfirmCompleteModal(false)}
            />

            <Container style={{ width: "100%", paddingLeft: "0px", paddingRight: "0px" }}>
                <Box sx={{ mb: 2 }} />

                <WorkOrderHeader workOrder={workOrder as ExpandedWorkOrder} tasks={tasks} />

                <TechnicianEntryGroup workOrder={workOrder as ExpandedWorkOrder} />

                {workOrderActionStatus === "pending" && <Skeleton variant="rectangular" width={400} height={20} sx={{ mt: 1 }} />}

                {workOrderActionStatus !== "pending" && (
                    <Box>
                        {workOrder.status === "UN" && (
                            <>
                                <Button disabled>Unassign</Button>
                                <Button onClick={() => workOrderAction("dispatch")}>In Progress</Button>
                                <Button onClick={() => workOrderAction("submit")}>Submit</Button>
                                <Button onClick={handleCompleteWorkOrder}>Complete</Button>
                            </>
                        )}

                        {workOrder.status === "IP" && (
                            <>
                                <Button onClick={() => workOrderAction("unassign")}>Unassign</Button>
                                <Button disabled>In Progress</Button>
                                <Button onClick={() => workOrderAction("submit")}>Submit</Button>
                                <Button onClick={handleCompleteWorkOrder}>Complete</Button>
                            </>
                        )}

                        {workOrder.status === "SB" && (
                            <>
                                <Button onClick={() => workOrderAction("unassign")}>Unassign</Button>
                                <Button onClick={() => workOrderAction("dispatch")}>In Progress</Button>
                                <Button disabled>Submit</Button>
                                <Button onClick={handleCompleteWorkOrder}>Complete</Button>
                            </>
                        )}

                        {workOrder.status === "CM" && (
                            <>
                                <Button onClick={() => workOrderAction("unassign")}>Unassign</Button>
                                <Button onClick={() => workOrderAction("dispatch")}>In Progress</Button>
                                <Button onClick={() => workOrderAction("submit")}>Submit</Button>
                                <Button disabled>Complete</Button>
                            </>
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default OfficeWorkOrder;
